import type { ChatThread } from "types/ChatThread";
import type { Message } from "types/Message";
import { postThread } from "../methods/postThread";
import { putThreadMessages } from "../methods/putThreadMessages";
import httpErrorStatus from "utilities/httpErrorStatus";
import saveBlob from "utilities/saveBlob";
import { getEnvironmentOrDefault } from "utilities/environment";
import { PRODUCT_FILE_PREFIX } from "utilities/productName";

interface ExportFile {
  chatThreads: Record<string, ChatThread>;
  // Each thread's messages are stored under `messages-<threadId>`.
  // Untyped here because the legacy file shape allows arbitrary keys.
  [key: string]: unknown;
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  saveBlob(blob, filename);
}

/**
 * Exports a single chat thread and its messages to a JSON file. The on-disk
 * shape matches the legacy IDB-era export so old files remain importable.
 */
export function exportThreadToJson(
  chatThread: ChatThread,
  messages: Message[]
): void {
  const slug = chatThread.name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .slice(0, 60);
  const date = new Date().toISOString().split("T")[0];
  const exportData: ExportFile = {
    exportMeta: {
      environment: getEnvironmentOrDefault(),
    },
    chatThreads: { [chatThread.id]: chatThread },
    [`messages-${chatThread.id}`]: messages,
  };
  downloadJson(`${PRODUCT_FILE_PREFIX}-${slug}-${date}.json`, exportData);
}

export interface ImportResult {
  imported: number;
  skipped: number;
  failed: number;
}

export interface ImportProgress {
  total: number;
  imported: number;
  skipped: number;
  failed: number;
  /**
   * Name of the thread currently being uploaded. Empty between threads
   * and on the first emit (before any thread has started).
   */
  currentThreadName: string;
}

/**
 * Imports a legacy export file: POSTs each thread, then PUTs its messages.
 * Threads whose id already exists on the server are skipped (POST returns
 * 409). Returns counts so the UI can render a result summary.
 *
 * `onProgress` is called once before the loop with the parsed `total`, then
 * again before each thread's POST attempt (with `currentThreadName` set), and
 * once more after each thread finishes (with `currentThreadName` reset to
 * `""`). Callers can drive a live progress UI from these emits.
 */
export async function importThreadsFromJson(
  file: File,
  getAccessToken: () => Promise<string>,
  onProgress?: (state: ImportProgress) => void
): Promise<ImportResult> {
  const fileText = await file.text();
  const parsed: unknown = JSON.parse(fileText);

  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Invalid import file: expected a JSON object");
  }

  const root = parsed as Record<string, unknown>;
  const chatThreads = root.chatThreads;
  if (
    chatThreads === null ||
    typeof chatThreads !== "object" ||
    Array.isArray(chatThreads)
  ) {
    throw new Error("Invalid import file: missing 'chatThreads' object");
  }

  const threads = Object.values(chatThreads as Record<string, ChatThread>);
  const progress: ImportProgress = {
    total: threads.length,
    imported: 0,
    skipped: 0,
    failed: 0,
    currentThreadName: "",
  };
  // Re-emit a fresh object on every update so React's setState sees a new
  // reference and re-renders consumers.
  const emit = () => onProgress?.({ ...progress });
  emit();

  for (const thread of threads) {
    progress.currentThreadName = thread.name;
    emit();

    const messages =
      (root[`messages-${thread.id}`] as Message[] | undefined) ?? [];

    let createdThread: ChatThread;
    try {
      createdThread = await postThread(thread, getAccessToken);
    } catch (error: unknown) {
      const status = httpErrorStatus(error);
      if (status === 409) {
        progress.skipped += 1;
      } else {
        console.error(`Failed to import thread ${thread.id}:`, error);
        progress.failed += 1;
      }
      progress.currentThreadName = "";
      emit();
      continue;
    }

    try {
      await putThreadMessages(createdThread.id, messages, getAccessToken);
      progress.imported += 1;
    } catch (error: unknown) {
      console.error(
        `Created thread ${createdThread.id} but failed to PUT messages:`,
        error
      );
      progress.failed += 1;
    }
    progress.currentThreadName = "";
    emit();
  }

  return {
    imported: progress.imported,
    skipped: progress.skipped,
    failed: progress.failed,
  };
}
